import React from "react";

export type PriceMode = "full" | "msrp-only" | "none";

interface PriceLabelProps {
  priceDollars?: number;
  msrpDollars?: number;
  /**
   * `full` — wholesale price (bold) with MSRP struck through beside it.
   * `msrp-only` — logged-out / scrubbed: show MSRP only, never the wholesale price.
   * `none` — brand.com (not shoppable): render no price at all.
   */
  mode: PriceMode;
}

export const formatMoney = (value?: number): string | undefined => {
  if (value === undefined) return undefined;
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
};

export const PriceLabel: React.FC<PriceLabelProps> = ({
  priceDollars,
  msrpDollars,
  mode,
}) => {
  const price = formatMoney(priceDollars);
  const msrp = formatMoney(msrpDollars);

  // brand.com surface is visually rich but not shoppable — no prices shown.
  if (mode === "none") return null;

  if (mode === "msrp-only") {
    if (msrp === undefined) return null;
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-baseline gap-1.5">
          <span className="type-fs-paragraph-medium text-fs-text-primary">
            {msrp}
          </span>
          <span className="type-fs-label text-fs-text-subdued">MSRP</span>
        </div>
        <span className="type-fs-label text-fs-text-primary underline-offset-2 hover:underline">
          Sign in for WSP
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-baseline gap-1.5">
      <span className="type-fs-paragraph-medium text-fs-text-primary">
        {price ?? "—"}
      </span>
      {msrp !== undefined ? (
        <span className="type-fs-label text-fs-text-subdued">MSRP {msrp}</span>
      ) : null}
    </div>
  );
};
